<script setup>
/**
 * FormControlMProductsProductsCategoryId
 * Specialized product category menu dropdown picker.
 * Pulls operational metadata collections via dynamic API routes on load,
 * automatically implementing form validity default rules for required status profiles.
 */
import { ref, onMounted, getCurrentInstance } from 'vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State Layers ---

const options = ref([]);

// For required select menus to fail checkValidity they must have a blank value.
const unsetValue = props.field?.required ? '' : 0;

// --- Methods ---

/**
 * Aggregates all registered top-level product category collections asynchronously,
 * pre-populating standard blank validation headers before hydrating available entities.
 */
const setOptions = async () => {
    try {
        const response = await store.dispatch('request/get', {
            url: 'api/component/m_products_categories',
            params: {
                parentId: 0,
                sort: 'title',
            }
        });

        const menuOptions = [{
            value: unsetValue,
            text: 'Select…',
        }];

        const items = response?.data?.items || [];

        items.forEach((item) => {
            menuOptions.push({
                value: item.id,
                text: item.title,
            });
        });

        options.value = menuOptions;
    } catch (err) {
        console.error('Critical failure retrieving category layout catalog data:', err);
        options.value = [{ value: unsetValue, text: 'Select…' }];
    }
};

// --- Lifecycle ---

onMounted(async () => {
    if (props.field?.name) {
        await store.dispatch(`${props.formId}/setDefaultValue`, {
            name: props.field.name,
            value: unsetValue,
        });
    }

    await setOptions();
});
</script>

<template>
    <select
        :id="field.id"
        v-model="currentData[field.name]"
        :name="field.name"
        :required="field.required"
        class="form-select"
    >
        <option v-for="option in options" :key="option.value" :value="option.value">
            {{ option.text }}
        </option>
    </select>
</template>